import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import CustomerHeader from "@/components/CustomerHeader";
import { Coins, MessageCircle, Wallet, TrendingUp, Loader2, ChevronRight, PlusCircle, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function FalciPanel() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("pending");
  const [consults, setConsults] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showWD, setShowWD] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [wdAmount, setWdAmount] = useState(250);

  const load = useCallback(async () => {
    try {
      const [meRes, earnRes, wdRes] = await Promise.all([
        api.get("/falci/me"),
        api.get("/falci/earnings"),
        api.get("/falci/withdrawals"),
      ]);
      setMe(meRes.data);
      setEarnings(earnRes.data);
      setWithdrawals(wdRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yükleme başarısız");
      if (e.response?.status === 403) nav("/panel");
    }
  }, [nav]);

  const loadConsults = useCallback(async (status) => {
    const params = status && status !== "all" ? `?status=${status}` : "";
    const { data } = await api.get(`/falci/consultations${params}`);
    setConsults(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "earnings") return;
    const s = tab === "all" ? "" : tab;
    loadConsults(s);
    const t = setInterval(() => loadConsults(s), 10000);
    return () => clearInterval(t);
  }, [tab, loadConsults]);

  const toggleOnline = async () => {
    try {
      const { data } = await api.patch("/falci/me", { is_online: !me.is_online });
      setMe(data);
      toast.success(data.is_online ? "Çevrimiçi görünüyorsun" : "Çevrimdışı");
    } catch { toast.error("Değiştirilemedi"); }
  };

  const requestWD = async () => {
    try {
      await api.post("/falci/withdrawals", { amount: parseFloat(wdAmount), iban: me.iban, bank_name: me.bank_name, account_holder: me.account_holder });
      toast.success("Çekim talebi oluşturuldu");
      setShowWD(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Talep başarısız"); }
  };

  const saveBank = async (payload) => {
    try {
      const { data } = await api.patch("/falci/me", payload);
      setMe(data);
      toast.success("Banka bilgileri güncellendi");
      setShowBank(false);
    } catch { toast.error("Kaydedilemedi"); }
  };

  if (!me) return <div className="cosmic-bg min-h-screen flex items-center justify-center"><div className="stars"/><Loader2 className="w-8 h-8 animate-spin gold-text" /></div>;

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="falci-panel">
        {/* Hero */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif-myst text-3xl md:text-4xl">Falcı Paneli</h1>
            <p className="text-white/60 text-sm">Hoşgeldin {me.name}</p>
          </div>
          <button onClick={toggleOnline} data-testid="toggle-online" className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 ${me.is_online ? "bg-emerald-500/20 text-emerald-200" : "bg-white/5 text-white/50"}`}>
            <span className={`w-2 h-2 rounded-full ${me.is_online ? "bg-emerald-400 animate-pulse" : "bg-white/40"}`} />
            {me.is_online ? "Çevrimiçi" : "Çevrimdışı"}
          </button>
        </div>

        {/* Kazanç kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={<Coins className="w-5 h-5" />} label="Bakiye" value={`${Math.round(earnings?.available_balance || 0)} ₺`} testid="stat-balance" highlight />
          <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Bu Ay" value={`${earnings?.month_earnings || 0} ₺`} testid="stat-month" />
          <StatCard icon={<Wallet className="w-5 h-5" />} label="Toplam Kazanç" value={`${earnings?.lifetime_earnings || 0} ₺`} testid="stat-lifetime" />
          <StatCard icon={<MessageCircle className="w-5 h-5" />} label="Tamamlanan" value={earnings?.total_completed || 0} testid="stat-completed" />
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setShowWD(true)} data-testid="open-withdrawal" className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-2"><PlusCircle className="w-4 h-4" /> Çekim Talebi</button>
          <button onClick={() => setShowBank(true)} data-testid="edit-bank" className="btn-ghost px-4 py-2 rounded-full text-sm flex items-center gap-2"><Landmark className="w-4 h-4" /> Banka Bilgileri</button>
        </div>

        {/* Tabs */}
        <div className="glass rounded-full p-1 inline-flex mb-4 flex-wrap">
          {[["pending","Bekleyen"],["in_progress","Aktif"],["completed","Tamamlanan"],["all","Tümü"],["earnings","Çekimler"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm ${tab === k ? "btn-gold" : "text-white/60 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {tab === "earnings" ? (
          <div className="glass rounded-2xl overflow-hidden" data-testid="withdrawal-list">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60"><tr><th className="text-left px-4 py-2">Tarih</th><th className="text-left px-4 py-2">Tutar</th><th className="text-left px-4 py-2">Durum</th><th className="text-left px-4 py-2">IBAN</th></tr></thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id} className="border-t border-white/5">
                    <td className="px-4 py-2 text-white/70">{new Date(w.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2 gold-text">{w.amount_try} ₺</td>
                    <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${w.status === "paid" ? "bg-emerald-500/20 text-emerald-200" : w.status === "rejected" ? "bg-red-500/20 text-red-200" : "bg-amber-500/20 text-amber-200"}`}>{w.status}</span></td>
                    <td className="px-4 py-2 text-white/50 text-xs">{w.iban}</td>
                  </tr>
                ))}
                {withdrawals.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-white/40">Henüz çekim yok.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-2" data-testid="consultation-list">
            {consults.map((c) => (
              <button key={c.id} onClick={() => nav(`/sohbet/${c.id}`)} data-testid={`consult-${c.id}`} className="w-full glass rounded-xl px-4 py-3 flex items-center justify-between hover:border-[#D4AF37]/40 border border-transparent transition">
                <div className="text-left">
                  <div className="text-sm font-semibold">{c.fortune_type} • {c.tokens_charged} jeton</div>
                  <div className="text-xs text-white/50">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={c.status} />
                  <ChevronRight className="w-4 h-4 text-white/40" />
                </div>
              </button>
            ))}
            {consults.length === 0 && <div className="text-center text-white/40 py-16">Bu sekmede kayıt yok.</div>}
          </div>
        )}

        {/* Withdrawal Modal */}
        {showWD && (
          <Modal onClose={() => setShowWD(false)}>
            <h2 className="font-serif-myst text-2xl mb-3">Çekim Talebi</h2>
            <p className="text-white/60 text-sm mb-4">Minimum <span className="gold-text">250 ₺</span>. Onay sonrası 2-3 iş gününde hesabına aktarılır.</p>
            <label className="text-xs text-white/60">Tutar (₺)</label>
            <input data-testid="wd-amount" type="number" min={250} max={earnings?.available_balance || 0} value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white" />
            <div className="text-xs text-white/50 mt-1">Mevcut bakiye: {Math.round(earnings?.available_balance || 0)} ₺</div>
            {!me.iban && <div className="mt-3 text-xs text-red-300">Önce banka bilgilerini eklemelisin.</div>}
            <div className="flex gap-2 mt-4">
              <button onClick={requestWD} disabled={!me.iban} data-testid="wd-submit" className="btn-gold flex-1 py-2.5 rounded-full">Gönder</button>
              <button onClick={() => setShowWD(false)} className="btn-ghost px-5 rounded-full">İptal</button>
            </div>
          </Modal>
        )}
        {showBank && <BankModal me={me} onClose={() => setShowBank(false)} onSave={saveBank} />}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, highlight, testid }) {
  return (
    <div className={`glass rounded-2xl p-4 ${highlight ? "gold-border" : ""}`} data-testid={testid}>
      <div className={`${highlight ? "gold-text" : "text-white/50"}`}>{icon}</div>
      <div className="text-xs text-white/50 mt-2">{label}</div>
      <div className="font-serif-myst text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ["bg-amber-500/20 text-amber-200", "Bekliyor"],
    in_progress: ["bg-emerald-500/20 text-emerald-200", "Aktif"],
    completed: ["bg-white/10 text-white/70", "Tamam"],
    cancelled: ["bg-red-500/20 text-red-200", "İptal"],
    expired: ["bg-white/5 text-white/40", "Süresi Doldu"],
  };
  const [cls, label] = map[status] || ["bg-white/10", status];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="glass-strong rounded-3xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function BankModal({ me, onClose, onSave }) {
  const [iban, setIban] = useState(me.iban || "");
  const [bank, setBank] = useState(me.bank_name || "");
  const [holder, setHolder] = useState(me.account_holder || "");
  return (
    <Modal onClose={onClose}>
      <h2 className="font-serif-myst text-2xl mb-3">Banka Bilgileri</h2>
      <label className="text-xs text-white/60">IBAN</label>
      <input data-testid="bank-iban" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} placeholder="TR00 0000 0000 0000 0000 00" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white mb-2" />
      <label className="text-xs text-white/60">Banka Adı</label>
      <input data-testid="bank-name" value={bank} onChange={(e) => setBank(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white mb-2" />
      <label className="text-xs text-white/60">Hesap Sahibi</label>
      <input data-testid="bank-holder" value={holder} onChange={(e) => setHolder(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white" />
      <div className="flex gap-2 mt-4">
        <button onClick={() => onSave({ iban, bank_name: bank, account_holder: holder })} data-testid="bank-save" className="btn-gold flex-1 py-2.5 rounded-full">Kaydet</button>
        <button onClick={onClose} className="btn-ghost px-5 rounded-full">İptal</button>
      </div>
    </Modal>
  );
}
